// components/views/claim/ClaimForm.jsx
import React, { Component } from 'react';

class ClaimForm extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      businessName: '',
      password: '',
      confirmPassword: '',
      errors: {},
      formSubmitted: false,
      currentSection: 1 // 1: Personal, 2: Business, 3: Security
    };
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({ 
      [name]: value,
      errors: { ...this.state.errors, [name]: null } // Clear error when user types
    });
  };

  validateSection = (section) => {
    const { firstName, lastName, email, phone, businessName, password, confirmPassword } = this.state;
    const errors = {};

    if (section === 1) {
      // Personal Information
      if (!firstName.trim()) errors.firstName = 'First name is required';
      if (!lastName.trim()) errors.lastName = 'Last name is required';
      if (!email.trim()) errors.email = 'Email is required';
      if (!phone.trim()) errors.phone = 'Phone number is required';

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (email && !emailRegex.test(email)) {
        errors.email = 'Please enter a valid email address';
      }

      // Phone validation
      const phoneRegex = /^\(?[\d\s\-\(\)\.]{10,}$/;
      if (phone && !phoneRegex.test(phone)) {
        errors.phone = 'Please enter a valid phone number';
      }
    }

    if (section === 2) {
      // Business Information
      if (!businessName.trim()) errors.businessName = 'Business name is required';
    }

    if (section === 3) {
      // Security
      if (!password) errors.password = 'Password is required';
      if (!confirmPassword) errors.confirmPassword = 'Please confirm your password';

      // Password validation
      if (password && password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }

      // Password confirmation
      if (password && confirmPassword && password !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    return errors;
  };

  handleNextSection = () => {
    const errors = this.validateSection(this.state.currentSection);
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }
    
    this.setState({ 
      currentSection: this.state.currentSection + 1,
      errors: {}
    });
  };

  handlePrevSection = () => {
    this.setState({ 
      currentSection: this.state.currentSection - 1,
      errors: {}
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.setState({ formSubmitted: true });

    const errors = this.validateSection(3); // Validate final section
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    // Prepare prospect data
    const prospectData = {
      firstName: this.state.firstName.trim(),
      lastName: this.state.lastName.trim(),
      email: this.state.email.trim().toLowerCase(),
      phone: this.state.phone.trim(),
      businessName: this.state.businessName.trim(),
      password: this.state.password
    };

    this.props.onSubmit(prospectData);
  };

  render() {
    const { jobDetails, onBack, loading, error } = this.props;
    const { 
      firstName, 
      lastName, 
      email, 
      phone, 
      businessName, 
      password, 
      confirmPassword, 
      errors, 
      formSubmitted,
      currentSection
    } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-6">
          <div className="max-w-md mx-auto text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-white bg-opacity-20 rounded-full mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <h1 className="text-xl font-bold mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Claim Your ${jobDetails?.jobTotal} Job
            </h1>
            <p className="text-teal-100" style={{fontFamily: 'Lato, sans-serif'}}>
              Quick registration to get started
            </p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="px-4 py-4 bg-white border-b">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === currentSection 
                      ? 'bg-teal-500 text-white' 
                      : step < currentSection 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step < currentSection ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    ) : (
                      step
                    )}
                  </div>
                  {step < 3 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      step < currentSection ? 'bg-green-500' : 'bg-gray-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-xs text-gray-500" style={{fontFamily: 'Lato, sans-serif'}}>Personal</span>
              <span className="text-xs text-gray-500" style={{fontFamily: 'Lato, sans-serif'}}>Business</span>
              <span className="text-xs text-gray-500" style={{fontFamily: 'Lato, sans-serif'}}>Security</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="px-4 py-4">
            <div className="max-w-md mx-auto">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-red-800 text-sm" style={{fontFamily: 'Lato, sans-serif'}}>{error}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-4 py-6">
          <div className="max-w-md mx-auto">
            
            {/* Section 1: Personal Information */}
            {currentSection === 1 && (
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Personal Information
                </h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={firstName}
                        onChange={this.handleInputChange}
                        disabled={loading}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                          errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        style={{fontFamily: 'Lato, sans-serif'}}
                        placeholder="John"
                      />
                      {errors.firstName && (
                        <p className="text-red-600 text-xs mt-1" style={{fontFamily: 'Lato, sans-serif'}}>{errors.firstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={lastName}
                        onChange={this.handleInputChange}
                        disabled={loading}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                          errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        style={{fontFamily: 'Lato, sans-serif'}}
                        placeholder="Smith"
                      />
                      {errors.lastName && (
                        <p className="text-red-600 text-xs mt-1" style={{fontFamily: 'Lato, sans-serif'}}>{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={this.handleInputChange}
                      disabled={loading}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      style={{fontFamily: 'Lato, sans-serif'}}
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="text-red-600 text-xs mt-1" style={{fontFamily: 'Lato, sans-serif'}}>{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={phone}
                      onChange={this.handleInputChange}
                      disabled={loading}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      style={{fontFamily: 'Lato, sans-serif'}}
                      placeholder="(555) 123-4567"
                    />
                    {errors.phone && (
                      <p className="text-red-600 text-xs mt-1" style={{fontFamily: 'Lato, sans-serif'}}>{errors.phone}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Section 2: Business Information */}
            {currentSection === 2 && (
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Business Information
                </h2>
                
                <div>
                  <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Business Name
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={businessName}
                    onChange={this.handleInputChange}
                    disabled={loading}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.businessName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    style={{fontFamily: 'Lato, sans-serif'}}
                    placeholder="Smith Auto Detailing LLC"
                  />
                  {errors.businessName && (
                    <p className="text-red-600 text-xs mt-1" style={{fontFamily: 'Lato, sans-serif'}}>{errors.businessName}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-2" style={{fontFamily: 'Lato, sans-serif'}}>
                    This will appear on invoices and dealer communications
                  </p>
                </div>

                <div className="mt-6 bg-blue-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-800 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Business Account Benefits
                  </h3>
                  <ul className="text-xs text-blue-700 space-y-1" style={{fontFamily: 'Lato, sans-serif'}}>
                    <li>• Professional invoicing with your business name</li>
                    <li>• Track earnings across multiple jobs</li>
                    <li>• Build reputation in the Jobvious network</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Section 3: Security */}
            {currentSection === 3 && (
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Account Security
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={password}
                      onChange={this.handleInputChange}
                      disabled={loading}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      style={{fontFamily: 'Lato, sans-serif'}}
                      placeholder="Minimum 8 characters"
                    />
                    {errors.password && (
                      <p className="text-red-600 text-xs mt-1" style={{fontFamily: 'Lato, sans-serif'}}>{errors.password}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={this.handleInputChange}
                      disabled={loading}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      style={{fontFamily: 'Lato, sans-serif'}}
                      placeholder="Re-enter your password"
                    />
                    {errors.confirmPassword && (
                      <p className="text-red-600 text-xs mt-1" style={{fontFamily: 'Lato, sans-serif'}}>{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 bg-green-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-green-800 mb-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        You're Almost Done!
                      </h4>
                      <p className="text-xs text-green-700" style={{fontFamily: 'Lato, sans-serif'}}>
                        After claiming, you'll receive dealer details and can begin coordinating pickup immediately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex space-x-3 mb-6">
              {currentSection === 1 ? (
                <button 
                  type="button"
                  onClick={onBack}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-colors"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  <span className="flex items-center justify-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                    Back to Terms
                  </span>
                </button>
              ) : (
                <button 
                  type="button"
                  onClick={this.handlePrevSection}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-colors"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  <span className="flex items-center justify-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                    Previous
                  </span>
                </button>
              )}
              
              {currentSection < 3 ? (
                <button 
                  type="button"
                  onClick={this.handleNextSection}
                  className="flex-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all"
                  style={{fontFamily: 'Montserrat, sans-serif', minWidth: '150px'}}
                >
                  <span className="flex items-center justify-center">
                    Continue
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </span>
                </button>
              ) : (
                <button 
                  type="button"
                  onClick={this.handleSubmit}
                  disabled={loading}
                  className="flex-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transform transition hover:scale-105 focus:outline-none focus:ring-4 focus:ring-orange-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{fontFamily: 'Montserrat, sans-serif', minWidth: '200px'}}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Claiming Job...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Claim This Job
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Trust Footer */}
            <div className="text-center pb-6">
              <div className="flex items-center justify-center space-x-2 text-gray-400 text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                </svg>
                <span style={{fontFamily: 'Lato, sans-serif'}}>Your information is secure and protected</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ClaimForm;